import { useState, useEffect } from 'react';
import { getApiKey, getAnthropicApiKey } from '../../../utils/storage';

import { UsageDataSource } from '../types';
import { DATA_SOURCE_STORAGE_KEY } from '../constants';

export interface UseDataSourceParams {
  hasOpenAiKeyProp?: boolean;
  hasAnthropicKeyProp?: boolean;
}

// Storage access is wrapped so a missing/blocked Web Storage (private mode,
// disabled cookies, non-browser test envs) degrades gracefully instead of
// throwing during render.
function readDataSourcePreference(): string | null {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    return window.localStorage.getItem(DATA_SOURCE_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeDataSourcePreference(value: string): void {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    window.localStorage.setItem(DATA_SOURCE_STORAGE_KEY, value);
  } catch {
    /* storage unavailable — preference simply not persisted */
  }
}

export interface UseDataSourceReturn {
  dataSource: UsageDataSource;
  setDataSource: React.Dispatch<React.SetStateAction<UsageDataSource>>;
  resolvedHasOpenAiKey: boolean;
  resolvedHasAnthropicKey: boolean;
  sourceDisplayName: string;
  hasBothProviders: boolean;
  includesAnthropic: boolean;
}

export function useDataSource({
  hasOpenAiKeyProp,
  hasAnthropicKeyProp,
}: UseDataSourceParams): UseDataSourceReturn {
  const resolvedHasOpenAiKey = typeof hasOpenAiKeyProp === 'boolean'
    ? hasOpenAiKeyProp
    : Boolean(getApiKey());
  const resolvedHasAnthropicKey = typeof hasAnthropicKeyProp === 'boolean'
    ? hasAnthropicKeyProp
    : Boolean(getAnthropicApiKey());

  const [dataSource, setDataSource] = useState<UsageDataSource>(() => {
    if (typeof window !== 'undefined') {
      const stored = readDataSourcePreference();
      if (stored === 'all' && resolvedHasOpenAiKey && resolvedHasAnthropicKey) {
        return 'all';
      }
      if (stored === 'openai' && resolvedHasOpenAiKey) {
        return 'openai';
      }
      if (stored === 'anthropic' && resolvedHasAnthropicKey) {
        return 'anthropic';
      }
    }
    if (resolvedHasOpenAiKey && resolvedHasAnthropicKey) return 'all';
    if (resolvedHasOpenAiKey) return 'openai';
    if (resolvedHasAnthropicKey) return 'anthropic';
    return 'openai';
  });

  useEffect(() => {
    writeDataSourcePreference(dataSource);
  }, [dataSource]);

  useEffect(() => {
    if (dataSource === 'all' && (!resolvedHasOpenAiKey || !resolvedHasAnthropicKey)) {
      if (resolvedHasOpenAiKey) {
        setDataSource('openai');
      } else if (resolvedHasAnthropicKey) {
        setDataSource('anthropic');
      }
    } else if (dataSource === 'openai' && !resolvedHasOpenAiKey && resolvedHasAnthropicKey) {
      setDataSource('anthropic');
    } else if (dataSource === 'anthropic' && !resolvedHasAnthropicKey && resolvedHasOpenAiKey) {
      setDataSource('openai');
    }
  }, [dataSource, resolvedHasOpenAiKey, resolvedHasAnthropicKey]);

  const sourceDisplayName = dataSource === 'openai'
    ? 'OpenAI'
    : dataSource === 'anthropic'
      ? 'Anthropic'
      : 'All Providers';
  const hasBothProviders = resolvedHasOpenAiKey && resolvedHasAnthropicKey;
  const includesAnthropic = dataSource === 'anthropic' || dataSource === 'all';

  return {
    dataSource,
    setDataSource,
    resolvedHasOpenAiKey,
    resolvedHasAnthropicKey,
    sourceDisplayName,
    hasBothProviders,
    includesAnthropic,
  };
}
